import "core-js/actual/structured-clone";
import { DataView as TableDataView, simpleTableData } from "@zebrabi/table-data";
import { DataView as MatrixDataView, DataViewMatrixNode, DataViewMetadataColumn, simpleMatrixData } from "@zebrabi/matrix-data";
import type { SheetData, RowData } from "x-data-spreadsheet";
import { stringWithCommaToFloat, validateTableData, validateTableDataForSmallMultiples } from "@zebrabi/table-data/helpers/tableDataHelper";
import {
    getOfficeStorageMatrixData,
    getOfficeStorageTableData,
    getOfficeStorageXSpreadsheetData,
    setOfficeStorageXSpreadsheetData,
} from "@zebrabi/table-data/helpers/officeStorageHelper";
import { parseColumnsAsGroupsDataView, parseShapedDataView } from "@zebrabi/data-helpers/hirarchyPPTparsingHelpers";
import { shapeRows } from "@zebrabi/data-helpers/groupByColumn";
import { setMeasureRoleByName } from "@zebrabi/data-helpers/tablesMetadata";
import { getLastParsedSheet, getTablesParseOptions, setLastParsedSheet, TablesParseOptions } from "../../store/parseOptionsStore";
import { _chartMap } from "./tempChartMap";
import { ChartChooserStore } from "../../../chart-chooser/store";
import { TablesChooserStore } from "@zebrabi/tables-chooser";
import { _tablesMap } from "./tempTablesMap";
import { MeasureRoles, tryGetMeasureRoleFromName } from "@zebrabi/data-helpers/fieldAssignment";
import { getOfficeSettings } from "@zebrabi/office-settings";
import { tryParseHierarchicalDataView } from "@zebrabi/data-helpers/hirarchyPPTparsingHelpers";
// TableData

function xSpreadsheetRowsToTableDataCategories(rows: SheetData["rows"]): string[] {
    delete rows["len"];
    return Object.values(rows).reduce((categoryArray: any[], row: RowData, index: number) => {
        if (index === 0) return categoryArray;

        const cells = Object.values(row?.cells || []);
        const nextCategory = cells[0]?.text;

        return [...categoryArray, nextCategory ? nextCategory : null];
    }, []);
}

function xSpreadsheetRowsToTableDataValues(rows: SheetData["rows"]): (string | number | null)[][] {
    delete rows["len"];
    return Object.values(rows)
        .filter((row) => row.cells[0].text)
        .reduce((valueColumnsArray: (number | null)[][] | [], row: RowData, index: number) => {
            if (index === 0) return valueColumnsArray;
            const cells = Object.values(row?.cells || []);
            const values: (string | number | null)[] = cells
                .filter((_cell, index) => index !== 0) // Skip category cell
                .map((cell) =>
                    cell.text && cell.text !== "" ? stringWithCommaToFloat(cell.text[0] === "=" ? cell.calculatedValue : cell.text) : null
                )
                .map((cell) => (typeof cell === "string" ? cell.trim() : cell));

            while (values.length < valueColumnsArray.length) {
                values.push(null);
            }

            if (values.length === 0) return valueColumnsArray;
            return values.map((value, index) => [...(valueColumnsArray[index] || []), value]);
        }, [])
        .filter((valuesArray) => valuesArray.some((value) => !!value));
}

function looksLikeCommentHeader(header: string): boolean {
    if (!header || !header.toLowerCase) {
        return false;
    }
    const headerLC = header.toLowerCase();
    return headerLC.startsWith("comm") || headerLC.startsWith("kom");
}

/**
 * Charts: measure roles by header (AC / PY / PL / FC / comments), then in order Values → PY → Plan → Forecast →
 * Comments; a text column the user dropped on Comments gets the Comments role regardless of its header.
 */
function assignChartMeasureRoles(headings: string[], commentByChoice: string = null): { roles: MeasureRoles[]; used: MeasureRoles[] } {
    let availableRoles = [MeasureRoles.Values, MeasureRoles.PreviousYear, MeasureRoles.Plan, MeasureRoles.Forecast, MeasureRoles.Comments];
    const used: MeasureRoles[] = [];
    const roles = headings.map((heading) => {
        const role = heading === commentByChoice ? MeasureRoles.Comments : tryGetMeasureRoleFromName(heading);
        if (role && !used.includes(role)) {
            used.push(role);
            availableRoles = availableRoles.filter((availableRole) => availableRole !== role);
            return role;
        }
        return null;
    });
    roles.forEach((role, index) => {
        if (!role) {
            const next = availableRoles.shift() ?? MeasureRoles.Values;
            roles[index] = next;
            used.push(next);
        }
    });
    return { roles, used };
}

/**
 * Charts (PowerPoint data editor): the sheet classified like the Tables one (docs/group-by-column.md), except that
 * Category is one column (column 0 unless the user chose another text column) and text columns are never measures.
 * The flat sheet is stored on every parse; a group column or a chosen category shapes the DataView in memory.
 */
export function xSpreadsheetToTableData(data: SheetData): TableDataView {
    const source = classifySheetColumns(data, [0], { maxCategories: 1, textMeasures: false });
    const { columns } = source;

    setLastParsedSheet(data);
    setOfficeStorageXSpreadsheetData(data);

    const measureColumns = columns.filter((column) => !column.isCategory && !column.isGroupBy && !column.excluded);
    const comment = measureColumns.find((column) => column.isComment);
    let dataView: TableDataView;
    if (getTablesParseOptions().columnsAsGroups && !columns.some((column) => column.isGroupBy)) {
        dataView = parseColumnsAsGroupsChartSheet(
            data,
            source.categoryIndexes[0],
            measureColumns.filter((column) => !column.isText && !column.isComment).map((column) => column.index)
        );
    } else if (source.shaped || hasRepeatedLabels(data, source.categoryIndexes[0])) {
        // a long-format sheet with no group column still carries several rows per category; they merge into one bar
        dataView = parseShapedChartSheet(
            data,
            source.categoryIndexes[0],
            measureColumns.map((column) => column.index),
            columns.find((column) => column.isGroupBy)?.index ?? -1,
            comment?.index ?? -1,
            comment?.isCommentByChoice ? comment.name : null
        );
    } else {
        const dropped = columns.filter((column) => column.excluded).map((column) => column.index);
        dataView = parseFlatChartSheet(
            dropped.length ? withoutColumns(data, dropped) : data,
            comment?.isCommentByChoice ? comment.name : null
        );
    }
    dataView.sourceValueColumns = columns.map(({ name, isComment, excluded, isText, isGroupBy, isCategory }) => ({
        name,
        isComment,
        excluded,
        isText,
        isGroupBy,
        isCategory,
    }));
    return dataView;
}

/** True when a non-empty label appears in more than one data row of the column */
function hasRepeatedLabels(data: SheetData, column: number): boolean {
    const seen = new Set<string>();
    return Object.keys(data.rows)
        .filter((key) => key !== "len")
        .map(Number)
        .filter((index) => index > 0)
        .some((index) => {
            const text = String(data.rows[index]?.cells?.[column]?.text ?? "").trim();
            if (text === "" || !seen.has(text)) {
                seen.add(text);
                return false;
            }
            return true;
        });
}

/** The data rows of a chart sheet (blank rows left out) and cell readers that resolve formulas */
function readChartSheet(data: SheetData) {
    const header: RowData["cells"] = data.rows?.[0]?.cells ?? {};
    const headerText = (column: number) => String(header[column]?.text ?? "");
    const cellText = (row: RowData, column: number): string => {
        const cell = row?.cells?.[column];
        const text = cell?.text ?? "";
        return text[0] === "=" ? (cell.calculatedValue ?? "") : text;
    };
    const rows: RowData[] = Object.keys(data.rows)
        .filter((key) => key !== "len")
        .map(Number)
        .filter((index) => index > 0)
        .sort((a, b) => a - b)
        .map((index) => data.rows[index])
        .filter((row) => Object.keys(row?.cells ?? {}).some((column) => cellText(row, Number(column)) !== ""));
    return { rows, headerText, cellText };
}

/**
 * Charts with "Columns (n)" in Group: rows merged by category (sums), then every measure column in use is a group of
 * its own. The output is the shape the legacy wide sheet produces (`buildSmallMultiplesTableData`): one value per
 * column carrying `source.groupName`, one Values measure named after the category.
 */
function parseColumnsAsGroupsChartSheet(data: SheetData, categoryColumn: number, measureColumns: number[]): TableDataView {
    const { rows, headerText, cellText } = readChartSheet(data);
    const categoryName = headerText(categoryColumn) || "Category";
    const headings = measureColumns.map(headerText);
    const shaped = shapeRows({
        rowLabels: rows.map((row) => [cellText(row, categoryColumn)]),
        values: rows.map((row) => measureColumns.map((column) => cellText(row, column))),
    });
    const dataView = validateTableDataForSmallMultiples({
        values: headings.map((heading, index) => ({
            source: { displayName: categoryName, groupName: heading },
            values: shaped.values.map((row) => row[index] ?? null),
        })),
        categories: [{ values: shaped.rowLabels.map((labels) => labels[0]), source: { displayName: categoryName } }],
        rowFields: [],
        columnFields: [],
        valueFields: headings,
        rowGrandTotal: false,
        columnGrandTotal: false,
        rowNames: [categoryName],
        columnNames: ["Group"],
        valueNames: [categoryName],
        metadata: {
            columns: [{ displayName: categoryName, roles: { Values: true }, type: { numeric: true }, queryName: categoryName, index: 0 }],
        },
    });
    dataView.columnsAsGroups = true;
    return dataView;
}

/**
 * Charts, shaped in memory: rows merged by category (sums), and with a group column one value column per group ×
 * measure carrying `source.groupName`, which is the shape the Excel pivot path produces and `grouped()` reads.
 */
function parseShapedChartSheet(
    data: SheetData,
    categoryColumn: number,
    measureColumns: number[],
    groupByColumn: number,
    commentColumn: number,
    commentByChoice: string
): TableDataView {
    const { rows, headerText, cellText } = readChartSheet(data);
    const categoryName = headerText(categoryColumn) || "Category";
    const measureNames = measureColumns.map(headerText);
    const commentIndex = measureColumns.indexOf(commentColumn);
    const shaped = shapeRows({
        rowLabels: rows.map((row) => [cellText(row, categoryColumn)]),
        values: rows.map((row) => measureColumns.map((column) => cellText(row, column))),
        groupCells: groupByColumn >= 0 ? rows.map((row) => cellText(row, groupByColumn)) : undefined,
        commentIndex,
    });
    const groups = groupByColumn >= 0 ? shaped.groups : [null];
    const measureCount = measureNames.length;

    const values: TableDataView["values"] = [];
    groups.forEach((group, g) => {
        measureNames.forEach((name, m) => {
            values.push({
                source: group === null ? { displayName: name } : { displayName: name, groupName: group },
                values: shaped.values.map((row) => row[g * measureCount + m] ?? null),
            });
        });
    });

    const { roles, used } = assignChartMeasureRoles(measureNames, commentByChoice);
    return validateTableData({
        values,
        categories: [{ values: shaped.rowLabels.map((labels) => labels[0]), source: { displayName: categoryName } }],
        rowFields: [],
        columnFields: groupByColumn >= 0 ? [headerText(groupByColumn)] : [],
        valueFields: measureNames,
        rowGrandTotal: false,
        columnGrandTotal: false,
        rowNames: [categoryName],
        columnNames: groupByColumn >= 0 ? [headerText(groupByColumn)] : [],
        valueNames: measureNames,
        metadata: {
            columns: measureNames.map((name, index) => ({
                displayName: name,
                roles: { [roles[index]]: true },
                type: roles[index] === MeasureRoles.Comments ? { text: true } : { numeric: true },
                queryName: name,
                index,
            })),
            measureRoles: used,
        },
    });
}

function parseFlatChartSheet(data: SheetData, commentByChoice: string = null): TableDataView {
    const categoriesName = Object.values(data.rows[0]?.cells || [])[0]?.text; // The top left cell
    const columnHeadings = Object.values(data.rows[0]?.cells || []) // First row without the first cell
        .filter((_cell, index) => index !== 0)
        .filter((cell) => !!cell?.text)
        .map((cell) => cell?.text);
    const categoryColumn = xSpreadsheetRowsToTableDataCategories(data.rows); // First column without the first row
    const valueColumns = xSpreadsheetRowsToTableDataValues(data.rows); // Other columns without the first row

    const { roles: columnRoles, used: usedRoles } = assignChartMeasureRoles(columnHeadings, commentByChoice);
    const isTextColumn = (heading: string) => looksLikeCommentHeader(heading) || heading === commentByChoice;

    return validateTableData({
        values: columnHeadings.map((heading, index) => ({
            source: { displayName: heading },
            values: valueColumns[index]?.map((vCol: any) => (isTextColumn(heading) ? vCol : isNaN(vCol) || vCol === "" ? null : vCol)),
        })),
        categories: [{ values: categoryColumn, source: { displayName: categoriesName } }],
        rowFields: [],
        columnFields: [],
        valueFields: columnHeadings,
        rowGrandTotal: false,
        columnGrandTotal: false,
        rowNames: [],
        columnNames: [],
        valueNames: columnHeadings,
        metadata: {
            columns: columnHeadings.map((heading, index) => ({
                displayName: heading,
                roles: { [columnRoles[index]]: true },
                type: columnRoles[index] === MeasureRoles.Comments ? { text: true } : { numeric: true },
                queryName: heading,
                index,
            })),
            measureRoles: usedRoles,
        },
    });
}

/**
 * Legacy charts small multiples (older decks, templates before the long-format samples): column 0 is the category,
 * every other column a group. Shown read-only in the Data fields list, with "Columns (n)" in Group.
 */
export function xSpreadsheetToTableDataforSmallMultiples(data: SheetData): TableDataView {
    const dataView = buildSmallMultiplesTableData(data);
    const header: RowData["cells"] = data.rows?.[0]?.cells ?? {};
    const names = Object.keys(header)
        .map(Number)
        .sort((a, b) => a - b)
        .map((index) => String(header[index]?.text ?? ""))
        .filter((name) => name !== "");
    dataView.columnsAsGroups = true;
    dataView.sourceValueColumns = names.map((name, i) => ({
        name,
        isComment: false,
        excluded: false,
        isText: i === 0,
        isGroupBy: false,
        isCategory: i === 0,
    }));
    return dataView;
}

function buildSmallMultiplesTableData(data: SheetData): TableDataView {
    const categoriesName = Object.values(data.rows[0]?.cells || [])[0]?.text; // The top left cell
    const columnHeadings = Object.values(data.rows[0]?.cells || []) // First row without the first cell
        .filter((_cell, index) => index !== 0)
        .filter((cell) => !!cell?.text)
        .map((cell) => cell?.text);
    const categoryColumn = xSpreadsheetRowsToTableDataCategories(data.rows); // First column without the first row
    const valueColumns = xSpreadsheetRowsToTableDataValues(data.rows); // Other columns without the first row
    return validateTableDataForSmallMultiples({
        values: [
            ...columnHeadings.map((heading, index) => ({
                source: { displayName: categoriesName, groupName: heading },
                values: valueColumns[index],
            })),
        ],
        categories: [{ values: categoryColumn, source: { displayName: categoriesName } }],
        rowFields: [],
        columnFields: [],
        valueFields: columnHeadings,
        rowGrandTotal: false,
        columnGrandTotal: false,
        rowNames: [categoriesName],
        columnNames: ["Group"],
        valueNames: [categoriesName],
        metadata: {
            columns: [
                {
                    displayName: categoriesName,
                    roles: { Values: true },
                    type: { numeric: true },
                    queryName: categoriesName,
                    index: 0,
                },
            ],
        },
    });
}

export function parseChartData(data: SheetData): TableDataView {
    return _chartMap[ChartChooserStore?.data?.value?.selectedChart]?.isMultiples
        ? xSpreadsheetToTableDataforSmallMultiples(data)
        : xSpreadsheetToTableData(data);
}

function getXSpreadSheetDataRow(index, rowName, valuesArray): Record<number, RowData> {
    return {
        [index]: {
            cells: {
                0: {
                    text: rowName,
                },
                ...valuesArray.reduce((valuesObject, value, index) => ({ ...valuesObject, [index + 1]: { text: value?.toString() } }), {}),
            },
        },
    };
}

export function tableDataToXSpreadsheet(tableData: TableDataView): SheetData {
    const rowNames = tableData.categories[0].values;
    const valuesArrays = rowNames.map((_rowName, index) => tableData.values.map((value) => (value.values ? value.values[index] : "")));
    return {
        rows: {
            ...getXSpreadSheetDataRow(0, tableData.categories[0].source.displayName, tableData.valueNames),
            ...rowNames.reduce(
                (valuesObject, rowName, index) => ({
                    ...valuesObject,
                    ...getXSpreadSheetDataRow(index + 1, rowName, valuesArrays[index]),
                }),
                {}
            ),
        },
    };
}

export function tableDataToXSpreadsheetForSmallMultiples(tableData: TableDataView): SheetData {
    const rowNames = tableData.categories[0].values;
    const valuesArrays = rowNames.map((_rowName, index) => tableData.values.map((value) => (value.values ? value.values[index] : "")));
    return {
        rows: {
            ...getXSpreadSheetDataRow(0, tableData.categories[0].source.displayName, tableData.valueFields),
            ...rowNames.reduce(
                (valuesObject, rowName, index) => ({
                    ...valuesObject,
                    ...getXSpreadSheetDataRow(index + 1, rowName, valuesArrays[index]),
                }),
                {}
            ),
        },
    };
}

export enum VisualType {
    CHARTS = "CHARTS",
    TABLES = "TABLES",
}

export const MIN_COLUMN_COUNT_FOR_SMALL_MULTIPLES = 5;
export const chartsSupportSmallMultiples = [
    "part-whole-structure-bar", //stacked bar chart
    "part-whole-time-column", //stacked column chart
    "part-whole-time-area", //stacked area chart
    "time-small-multiples-1", //"small multiples column chart"
];

export function getInitialXSpreadSheetData({ visualType }: { visualType: VisualType }): SheetData {
    if (visualType === VisualType.CHARTS) {
        //TODO to commit: this was a quick fix for when the number of values is lower than 5. For more details see https://zebrabi.atlassian.net/browse/ZVE-929
        const selectedChart: string = getOfficeSettings("ChartChooserStore")?.selectedChart.trim();
        const isSmallMultipleOrStacked = chartsSupportSmallMultiples.includes(selectedChart);
        const dataView = getOfficeStorageTableData();
        if (dataView?.columnsAsGroups || dataView?.values?.length >= MIN_COLUMN_COUNT_FOR_SMALL_MULTIPLES || isSmallMultipleOrStacked) {
            return tableDataToXSpreadsheetForSmallMultiples(dataView || simpleTableData);
        }
        return tableDataToXSpreadsheet(dataView || simpleTableData);
    }

    if (visualType === VisualType.TABLES) {
        try {
            // the flat source as typed (the last parse, else the stored sheet); the stored DataView may be pivoted or hierarchical
            const storedSheet = getLastParsedSheet() ?? getOfficeStorageXSpreadsheetData();
            if (storedSheet) {
                return storedSheet;
            }
            const dataView = getOfficeStorageMatrixData();
            if (dataView?.metadata.columns.filter((col) => col.roles.Category).length > 1) {
                // use for all cases, not only for hierarchical data?
                return getOfficeStorageXSpreadsheetData();
            }
            const isDataForSmallMultiples = !!dataView?.metadata.columns.find((col) => col.roles.Group);
            return isDataForSmallMultiples
                ? matrixDataToXSpreadsheetForSmallMultiples(dataView || simpleMatrixData)
                : matrixDataToXSpreadsheet(dataView || simpleMatrixData);
        } catch (error) {
            return getOfficeStorageXSpreadsheetData() ?? matrixDataToXSpreadsheet(simpleMatrixData);
        }
    }

    throw new Error("Visual type not supported");
}

export function concatCommentsToSheetData(data: SheetData, comments: SheetData): SheetData {
    const columnsCount = Object.keys(data.rows[0].cells).length;
    return {
        rows: {
            ...Object.fromEntries(
                Object.entries(data.rows).map(([key, value]) => [
                    key,
                    { cells: { ...value.cells, [columnsCount]: comments.rows[key]?.cells[0] || { text: "" } } },
                ])
            ),
        },
    };
}

// MatrixData

export function matrixDataToXSpreadsheet(matrixData: MatrixDataView): SheetData {
    return {
        rows: {
            ...getXSpreadSheetDataRow(0, matrixData.matrix.rows.levels[0]?.sources[0]?.displayName, matrixData.valueFields),
            ...matrixData.matrix.rows.root.children.reduce(
                (sheetRows, matrixRow, index) => ({
                    ...sheetRows,
                    [index + 1]: {
                        cells: {
                            0: { text: matrixRow.value },
                            ...Object.fromEntries(
                                Object.entries(matrixRow.values).map(([key, valueObject]) => [Number(key) + 1, { text: valueObject.value }])
                            ),
                        },
                    },
                }),
                {}
            ),
        },
    };
}

export function matrixDataToXSpreadsheetForSmallMultiples(matrixData: MatrixDataView): SheetData {
    return {
        rows: {
            ...getXSpreadSheetDataRow(
                0,
                matrixData.metadata.columns.find((col) => col.roles.Category)?.displayName,
                matrixData.matrix.columns.root.children.map((child) => child.value)
            ),
            ...matrixData.matrix.rows.root.children.reduce(
                (sheetRows, matrixRow, index) => ({
                    ...sheetRows,
                    [index + 1]: {
                        cells: {
                            0: { text: matrixRow.value },
                            ...Object.fromEntries(
                                Object.entries(matrixRow.values).map(([key, valueObject]) => [Number(key) + 1, { text: valueObject.value }])
                            ),
                        },
                    },
                }),
                {}
            ),
        },
    };
}

function columnHeadingsToMatrixValueSources(columnHeadings: string[]): DataViewMetadataColumn[] {
    return columnHeadings.map((columnHeading, index) => ({
        displayName: columnHeading,
        roles: { [tryGetMeasureRoleFromName(columnHeading) || MeasureRoles.Values]: true },
        type: looksLikeCommentHeader(columnHeading) ? { text: true } : { numeric: true },
        queryName: columnHeading,
        index,
    }));
}

function categoryAndValueColumnsToMatrixChildren(
    categoryColumn: string[],
    valueColumns: (string | number | null)[][],
    indexOfComments: number = -1,
    columnHeadings: string[],
    /** Measures that carry text (comments by the user's choice, text measures): their text passes through */
    textIndexes: number[] = []
): DataViewMatrixNode[] {
    return categoryColumn.map((category, index) => ({
        level: 0,
        value: category,
        values: valueColumns.reduce((values, _valueColumn, columnIndex) => {
            if (!columnHeadings[columnIndex]) return values;
            return {
                ...values,
                [columnIndex]: {
                    value:
                        columnIndex === indexOfComments || textIndexes.includes(columnIndex)
                            ? valueColumns[columnIndex][index]
                            : isNaN(valueColumns[columnIndex][index] as number) || valueColumns[columnIndex][index] === ""
                              ? null
                              : valueColumns[columnIndex][index],
                },
            };
        }, {}),
        isSubtotal: false,
    }));
}

export function parseTablesData(data: SheetData): MatrixDataView {
    return _tablesMap[TablesChooserStore?.data?.value?.selectedTable]?.isMultiples
        ? xSpreadsheetToMatrixDataForSmallMultiples(data)
        : xSpreadsheetToMatrixData(data);
}

interface SheetSourceColumn {
    /** Absolute column index in the sheet */
    index: number;
    name: string;
    isText: boolean;
    isCategory: boolean;
    isComment: boolean;
    /** Comments by the user's choice, not by header: the role auto-assignment can't see it */
    isCommentByChoice: boolean;
    isGroupBy: boolean;
    excluded: boolean;
}

interface SheetSource {
    columns: SheetSourceColumn[];
    /** The Category levels (absolute column indexes), in order */
    categoryIndexes: number[];
    /** The rows have to be shaped in memory (custom levels or a group column) instead of parsed as they are */
    shaped: boolean;
}

/**
 * Classifies the sheet's columns the way the Excel range reader does (docs/group-by-column.md): the Category levels
 * are the ones the user chose in the Data fields pane, else the leading text columns; a text column elsewhere is the
 * group column when dropped on Group, Comments by header or by the user's choice, otherwise left out; numeric
 * columns are measures unless unticked. Columns without a header are ignored.
 */
function classifySheetColumns(
    data: SheetData,
    /** The Category columns when the user made no choice: the leading text run (Tables), column 0 (Charts) */
    defaultCategoryIndexes: number[],
    rules: { maxCategories?: number; textMeasures?: boolean } = {}
): SheetSource {
    const header: RowData["cells"] = data.rows[0]?.cells ?? {};
    const columnCount = Math.max(-1, ...Object.keys(header).map(Number)) + 1;
    const rows: RowData[] = Object.keys(data.rows)
        .filter((key) => key !== "len")
        .map(Number)
        .filter((index) => index > 0)
        .map((index) => data.rows[index]);
    const options: TablesParseOptions = getTablesParseOptions();
    const excluded = new Set<string>(options.excludedColumns ?? []);
    const textMeasures = new Set<string>(rules.textMeasures === false ? [] : (options.textMeasureColumns ?? []));

    const names: string[] = [];
    const isText: boolean[] = [];
    for (let index = 0; index < columnCount; index++) {
        names.push(String(header[index]?.text ?? ""));
        const texts: string[] = rows.map((row) => String(row?.cells?.[index]?.text ?? "")).filter((text) => text !== "");
        // a column with nothing typed in yet stays a measure
        isText.push(texts.length > 0 && texts.every((text) => isNaN(Number(text))));
    }
    const groupByIndex = names.findIndex((name, index) => isText[index] && name !== "" && name === options.groupByColumn);
    const chosenCategories = (options.categoryColumns ?? [])
        .map((name) => names.indexOf(name))
        .filter((index) => index >= 0 && names[index] !== "" && isText[index] && index !== groupByIndex)
        .slice(0, rules.maxCategories ?? Infinity);
    const defaults = defaultCategoryIndexes.filter((index) => index !== groupByIndex && index < columnCount);
    const categoryIndexes = chosenCategories.length ? chosenCategories : defaults;
    const sameAsDefault = categoryIndexes.length === defaults.length && categoryIndexes.every((index, i) => index === defaults[i]);

    const columns: SheetSourceColumn[] = [];
    names.forEach((name, index) => {
        if (!name) {
            return;
        }
        const isCategory = categoryIndexes.includes(index);
        const isGroupBy = index === groupByIndex;
        const isComment = !isCategory && !isGroupBy && (looksLikeCommentHeader(name) || (isText[index] && name === options.commentColumn));
        // a text column is a measure only when the user dropped it on a value bucket
        const isTextMeasure = isText[index] && !isCategory && !isGroupBy && !isComment && textMeasures.has(name);
        columns.push({
            index,
            name,
            isText: isText[index],
            isCategory,
            isComment,
            isCommentByChoice: isComment && !looksLikeCommentHeader(name),
            isGroupBy,
            excluded: !isCategory && !isGroupBy && (excluded.has(name) || (isText[index] && !isComment && !isTextMeasure)),
        });
    });
    return { columns, categoryIndexes, shaped: (chosenCategories.length > 0 && !sameAsDefault) || groupByIndex >= 0 };
}

/** The sheet without the given columns, the remaining cells re-indexed to stay contiguous */
function withoutColumns(data: SheetData, dropped: number[]): SheetData {
    const droppedSet = new Set(dropped);
    const rows: SheetData["rows"] = {};
    Object.keys(data.rows)
        .filter((key) => key !== "len")
        .forEach((key) => {
            const row: RowData = data.rows[Number(key)];
            const cells: RowData["cells"] = {};
            let next = 0;
            const columnCount = Math.max(-1, ...Object.keys(row?.cells ?? {}).map(Number)) + 1;
            for (let column = 0; column < columnCount; column++) {
                if (droppedSet.has(column)) {
                    continue;
                }
                if (row.cells[column] !== undefined) {
                    cells[next] = row.cells[column];
                }
                next++;
            }
            rows[Number(key)] = { ...row, cells };
        });
    return { ...data, rows };
}

export function xSpreadsheetToMatrixData(data: SheetData): MatrixDataView {
    const leadingTextColumns = getNumberOfCategoryColumns(data);
    const source = classifySheetColumns(
        data,
        Array.from({ length: leadingTextColumns }, (_, index) => index)
    );
    const { columns } = source;

    // the flat sheet stays the editor's content and the source of every re-parse; only the DataView is shaped
    setLastParsedSheet(data);
    setOfficeStorageXSpreadsheetData(data);

    let dataView: MatrixDataView;
    const measureColumns = columns.filter((column) => !column.isCategory && !column.isGroupBy && !column.excluded);
    const comment = measureColumns.find((column) => column.isComment);
    if (getTablesParseOptions().columnsAsGroups && !columns.some((column) => column.isGroupBy)) {
        dataView = parseColumnsAsGroupsDataView(
            data,
            source.categoryIndexes,
            measureColumns.filter((column) => !column.isText && !column.isComment).map((column) => column.index)
        );
    } else if (source.shaped) {
        dataView = parseShapedDataView(
            data,
            source.categoryIndexes,
            measureColumns.map((column) => column.index),
            columns.find((column) => column.isGroupBy)?.index ?? -1,
            comment?.index ?? -1,
            measureColumns.filter((column) => column.isText && !column.isComment).map((column) => column.index),
            comment?.isCommentByChoice ?? false
        );
    } else {
        const dropped = columns.filter((column) => column.excluded).map((column) => column.index);
        // measures that carry text, as indexes among the kept non-category columns (the parser's value order)
        const textIndexes = measureColumns.map((column, i) => (column.isText ? i : -1)).filter((i) => i >= 0);
        dataView = parseFlatSheet(dropped.length ? withoutColumns(data, dropped) : data, leadingTextColumns, textIndexes);
        if (comment?.isCommentByChoice) {
            // the auto-assignment only knows comments by header
            setMeasureRoleByName(dataView, comment.name, MeasureRoles.Comments);
        }
    }
    dataView.sourceValueColumns = columns.map(({ name, isComment, excluded, isText, isGroupBy, isCategory }) => ({
        name,
        isComment,
        excluded,
        isText,
        isGroupBy,
        isCategory,
    }));
    return dataView;
}

function parseFlatSheet(data: SheetData, categoryColumns: number, textIndexes: number[] = []): MatrixDataView {
    if (categoryColumns > 1) {
        // use for all cases, not only for hierarchical data?
        const dv = tryParseHierarchicalDataView(data, categoryColumns);
        if (dv) {
            return dv;
        }
    }

    const categoriesName = Object.values(data.rows[0]?.cells || [])[0]?.text; // The top left cell
    // const categoriesNames = Object.values(data.rows[0]?.cells || []) //
    //   .filter((_cell, index) => index < categoryColumns)
    //   .filter((cell) => !!cell?.text)
    //   .map((cell) => cell?.text);

    const columnHeadings = Object.values(data.rows[0]?.cells || []) // First row without the first cell
        .filter((_cell, index) => index !== 0)
        .filter((cell) => !!cell?.text)
        .map((cell) => cell?.text);

    const categoryColumn = xSpreadsheetRowsToTableDataCategories(data.rows); // First column without the first row
    const valueColumns = xSpreadsheetRowsToTableDataValues(data.rows); // Other columns without the first row

    return {
        matrix: {
            columns: {
                levels: [
                    {
                        sources: columnHeadingsToMatrixValueSources(columnHeadings),
                    },
                ],
                root: {
                    children: [{ level: 0 }],
                },
            },
            rows: {
                levels: [
                    {
                        sources: [
                            {
                                displayName: categoriesName,
                                roles: { Category: true },
                                type: { text: true },
                                queryName: categoriesName,
                            },
                        ],
                    },
                ],
                root: {
                    children: categoryAndValueColumnsToMatrixChildren(
                        categoryColumn,
                        valueColumns,
                        columnHeadings.findIndex((heading) => looksLikeCommentHeader(heading)),
                        columnHeadings,
                        textIndexes
                    ),
                },
            },
            valueSources: columnHeadingsToMatrixValueSources(columnHeadings),
        },
        rowFields: [],
        columnFields: [],
        valueFields: columnHeadings,
        rowGrandTotal: false,
        columnGrandTotal: false,
        rowNames: [],
        columnNames: [],
        valueNames: columnHeadings,
        metadata: {
            columns: columnHeadings.map((heading, index) => {
                return {
                    displayName: heading,
                    roles: { [tryGetMeasureRoleFromName(heading) || MeasureRoles.Values]: true },
                    type: looksLikeCommentHeader(heading) ? { text: true } : { numeric: true },
                    queryName: heading,
                    index,
                };
            }),
        },
    };
}

/**
 * Cross table (the chooser's small-multiples template): the first column is the level, every other column a group.
 * The Data fields list shows that layout read-only: the members are ticked and locked, Group shows "Columns (n)".
 */
export function xSpreadsheetToMatrixDataForSmallMultiples(data: SheetData): MatrixDataView {
    const dataView = buildSmallMultiplesMatrixData(data);
    const header: RowData["cells"] = data.rows?.[0]?.cells ?? {};
    const names = Object.keys(header)
        .map(Number)
        .sort((a, b) => a - b)
        .map((index) => String(header[index]?.text ?? ""))
        .filter((name) => name !== "");
    dataView.columnsAsGroups = true;
    dataView.sourceValueColumns = names.map((name, i) => ({
        name,
        isComment: false,
        excluded: false,
        isText: i === 0,
        isGroupBy: false,
        isCategory: i === 0,
    }));
    return dataView;
}

function buildSmallMultiplesMatrixData(data: SheetData): MatrixDataView {
    //const categoriesName = Object.values(data.rows[0]?.cells || [])[0]?.text; // The top left cell
    const columnHeadings = Object.values(data.rows[0]?.cells || []) // First row without the first cell
        .filter((_cell, index) => index !== 0)
        .filter((cell) => !!cell?.text)
        .map((cell) => cell?.text);
    const categoryColumn = xSpreadsheetRowsToTableDataCategories(data.rows); // First column without the first row
    const valueColumns = xSpreadsheetRowsToTableDataValues(data.rows); // Other columns without the first row
    return {
        matrix: {
            columns: {
                levels: [
                    {
                        sources: [
                            {
                                displayName: "Group",
                                roles: {
                                    Group: true,
                                },
                                type: {
                                    text: true,
                                },
                                queryName: "Group",
                            },
                        ],
                    },
                ],
                root: {
                    children: columnHeadings.map((gn) => ({
                        level: 0,
                        value: gn,
                    })),
                },
            },
            rows: {
                levels: [
                    {
                        sources: [
                            {
                                displayName: "Category",
                                roles: {
                                    Category: true,
                                },
                                type: {
                                    text: true,
                                },
                                queryName: "Category",
                            },
                        ],
                    },
                ],
                root: {
                    children: categoryAndValueColumnsToMatrixChildren(
                        categoryColumn,
                        valueColumns,
                        columnHeadings.findIndex((heading) => looksLikeCommentHeader(heading)),
                        columnHeadings
                    ),
                },
            },
            valueSources: [
                {
                    displayName: "Sum of Revenue",
                    roles: {
                        Values: true,
                    },
                    type: {
                        numeric: true,
                    },
                    queryName: "Sum of Revenue",
                    index: 0,
                },
            ],
        },
        rowFields: [],
        columnFields: [],
        valueFields: ["Sum of Revenue"],
        rowGrandTotal: false,
        columnGrandTotal: false,
        rowNames: ["Category"],
        columnNames: ["Group"],
        valueNames: ["Sum of Revenue"],
        metadata: {
            columns: [
                {
                    displayName: "Category",
                    roles: {
                        Category: true,
                    },
                    type: {
                        text: true,
                    },
                    queryName: "Category",
                },
                {
                    displayName: "Group",
                    roles: {
                        Group: true,
                    },
                    type: {
                        text: true,
                    },
                    queryName: "Group",
                },
                {
                    displayName: "Sum of Revenue",
                    roles: {
                        Values: true,
                    },
                    type: {
                        numeric: true,
                    },
                    queryName: "Sum of Revenue",
                    index: 0,
                },
            ],
        },
    };
}

export function getNumberOfCategoryColumns(data: SheetData): number {
    if (!data?.rows || !data.rows[0]?.cells) {
        return 1;
    }

    delete data.rows["len"]; // this properts sometimes appears on the rows and is deleted so that it does not interfere with the calculations
    const sourceDataRows = Object.keys(data.rows).map((key) => {
        return data.rows[+key];
    });

    const firstRowsCells = sourceDataRows[0].cells;
    const firstRowsCellsKeys = Object.keys(firstRowsCells);
    const totalColumnsCount = firstRowsCellsKeys.length;
    let numberOfTextColumns = 0;
    for (let i = 0; i < totalColumnsCount; i++) {
        const isStringCol = sourceDataRows.every((row) => {
            const cell = row.cells[i];
            return isNaN(Number(cell?.text)) || cell?.text === ""; //typeof cell?.text === "string";
        });

        if (isStringCol) {
            numberOfTextColumns++;
        } else {
            break;
        }
    }

    return numberOfTextColumns;
}
